import { useEffect, useState } from 'react';
import vopayApi from '@/services/api.config';
import useStorage from '@/hooks/useStorage';
import { usePermissions } from '@/providers/Permissions.provider';

const { api } = vopayApi;

const CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

const tokenCache = new Map<string, { token: string; fetchedAt: number }>();
const inFlight = new Map<string, Promise<string | undefined>>();

async function findPartnerToken(loggedInAccountId: string, partnerAccountId: string): Promise<string | undefined> {
  const cacheKey = `${loggedInAccountId}::${partnerAccountId}`;

  const cached = tokenCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.token;

  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const res = await api.fetchPartnerPartners({ ShowToken: true, SearchText: partnerAccountId });
      if (!res.Success) throw new Error(res.ErrorMessage);
      const token = res.Partners.find((partner) => partner.PartnerAccountID === partnerAccountId)?.Token;
      if (token) tokenCache.set(cacheKey, { token, fetchedAt: Date.now() });

      return token;
    } finally {
      inFlight.delete(cacheKey);
    }
  })();

  inFlight.set(cacheKey, promise);

  return promise;
}

type PartnerBearer = {
  /** The partner's token; empty until the lookup resolves, and after one that produced none. */
  bearer: string;
  /**
   * False only while the lookup is in flight. A caller that waits on the token needs this to tell
   * "still looking" from "looked, and there is no token" — without it a partner whose lookup fails or
   * matches nothing leaves the caller waiting for good.
   */
  hasSettled: boolean;
};

/** `usePartnerBearer` with the lookup's state alongside the token, for callers that wait on it. */
export const usePartnerBearerLookup = (partnerAccountId: string): PartnerBearer => {
  const {
    login: { getPartnerLogin },
  } = useStorage();

  const { permissions } = usePermissions();
  const { accountId, JWT } = getPartnerLogin();

  const useSessionToken = !permissions.PartnerCreationEnabled || !partnerAccountId || partnerAccountId === accountId;
  const [lookup, setLookup] = useState({ token: useSessionToken ? JWT : '', hasSettled: useSessionToken });

  useEffect(() => {
    if (useSessionToken) {
      setLookup({ token: JWT, hasSettled: true });

      return;
    }

    let active = true;

    setLookup({ token: '', hasSettled: false });

    const resolve = async () => {
      try {
        const resolved = await findPartnerToken(accountId, partnerAccountId);
        if (active) setLookup({ token: resolved ?? '', hasSettled: true });
      } catch (error) {
        console.error(error);
        if (active) setLookup({ token: '', hasSettled: true });
      }
    };

    resolve();

    return () => {
      active = false;
    };
  }, [useSessionToken, partnerAccountId, JWT, accountId]);

  return { bearer: lookup.token, hasSettled: lookup.hasSettled };
};

/**
 * Bearer token for the partner identified by partnerAccountId: the session token when it's the
 * current (logged-in) partner, otherwise the matching partner's token (cached 3h per partner).
 */
const usePartnerBearer = (partnerAccountId: string): string => usePartnerBearerLookup(partnerAccountId).bearer;

export default usePartnerBearer;
